import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getFormValues } from '../actions/steps/utils';
import DISPLAY_ACTION from './types';
import useConditions from '../conditions';
import CONDITION_TYPES from '../conditions/types';
import useValidations from '../validations';

const useConditionalDisplay = ({ element, page, designMode }) => {
  const { evaluate } = useConditions();

  const { validate } = useValidations();
  const local = useSelector(state => state.Local);
  const user = useSelector(state => state.User);
  const { scenario } = useSelector(state => state.Content);
  const { documents, selections } = useSelector(state => state.DataLinks);
  const sequencer = useSelector(state => state.ActionSequences);

  const [conditionalDisplayResult, setConditionalDisplayResult] = useState(
    DISPLAY_ACTION.NO_ACTION
  );

  useEffect(() => {
    async function evaluateConditions() {
      if (
        !element.conditionType ||
        element.conditionType?.trim().length === 0
      ) {
        setConditionalDisplayResult(DISPLAY_ACTION.NO_ACTION);
        return;
      }

      const condition = {
        conditionType: element.conditionType,
        conditionValueSource: element.conditionValueSource,
        conditionValueDataSourceId: element.conditionValueDataSourceId,
        conditionValueDataSourceField: element.conditionValueDataSourceField,
        conditionDataSourceId: element.conditionDataSourceId,
        conditionDataSourceField: element.conditionDataSourceField,
        conditionDataSourceOperator: element.conditionDataSourceOperator,
        conditionDataSourceValue: element.conditionDataSourceValue,
        conditionJson: element.conditionJson,
        conditionLocalValueField: element.conditionLocalValueField,
        conditionLocalField: element.conditionLocalField,
        conditionLocalOperator: element.conditionLocalOperator,
        conditionLocalValue: element.conditionLocalValue,
        conditionStaticValue: element.conditionStaticValue,
        conditionSequencerField: element.conditionSequencerField,
        conditionSequencerOperator: element.conditionSequencerOperator,
        conditionSequencerValue: element.conditionSequencerValue
      };

      let matched, matchedIsFalse;

      const formsOnPage = window?.document?.getElementsByTagName('form');
      const forms = {};

      for (const form of formsOnPage) {
        forms[form.id] = getFormValues({ formId: form.id, returnObject: true });
      }

      if (condition.conditionType === CONDITION_TYPES.FORM_VALUE_IS_MATCHED) {
        condition.conditionDisplayElementId = element.conditionDisplayElementId;
        condition.conditionFormOperator = element.conditionFormOperator;
        condition.conditionFormValue = element.conditionFormValue;
        const form = forms[element.conditionDisplayFormId];
        if (form) {
          condition.conditionFormCurrentValue =
            form[element.conditionDisplayElementId];
        }
      }

      if (
        condition.conditionType === CONDITION_TYPES.JAVASCRIPT_FUNCTION_RESULT
      ) {
        try {
          // eslint-disable-next-line
          const computeFunction = new Function(
            'page',
            'selections',
            'documents',
            'local',
            'user',
            'forms',
            element.conditionDisplayFunctionCode
          );

          try {
            matched = computeFunction(
              page,
              scenario?.data?.selections || selections,
              documents,
              scenario?.data?.local || local,
              scenario?.data?.user || user,
              forms
            );
            matchedIsFalse = !matched;
          } catch (error) {
            console.error('useValidations.computeFunction', error);
            return false;
          }
        } catch (error) {
          matched = { error };
        }
      } else if (
        condition.conditionType === CONDITION_TYPES.FORM_FIELD_IS_NOT_VALID
      ) {
        matched = false;
        matchedIsFalse = true;

        if (
          local?.validations !== undefined &&
          local?.validations[element.invalidFormFieldDataLinkField]?.error ===
            true
        ) {
          matched = true;
          matchedIsFalse = false;
        }
      } else if (condition.conditionType === CONDITION_TYPES.FORM_IS_VALID) {
        const { validations } = await validate({
          page,
          formId: element.conditionDisplayFormId,
          preventDispatch: true
        });

        matched = true;
        matchedIsFalse = false;
        Object.keys(validations).forEach(key => {
          if (validations[key].error === true) {
            matched = false;
            matchedIsFalse = true;
          }
        });
      } else if (
        condition.conditionType === CONDITION_TYPES.FORM_IS_NOT_VALID
      ) {
        const { validations } = await validate({
          page,
          formId: element.conditionDisplayFormId,
          preventDispatch: true
        });

        matched = true;
        matchedIsFalse = false;
        let hasError = false;
        Object.keys(validations).forEach(key => {
          if (validations[key].error === true) {
            hasError = true;
          }
        });
        if (!hasError) {
          matched = false;
          matchedIsFalse = true;
        }
      } else {
        matched = await evaluate({
          condition,
          documents: scenario?.data?.documents || documents,
          selections: scenario?.data?.selections || selections,
          local: scenario?.data?.local || local,
          sequencer: scenario?.data?.sequencer || sequencer
        });

        matchedIsFalse =
          (!matched && !matched?.error) || matched?.matched === false;
      }

      const attributesAndClasses = {
        matched,
        matchedIsFalse,
        htmlElementId: element.conditionDisplayElementId,
        addClasses: matchedIsFalse
          ? element.conditionDisplayRemoveClasses || ''
          : element.conditionDisplayAddClasses || '',
        removeClasses: matchedIsFalse
          ? element.conditionDisplayAddClasses || ''
          : element.conditionDisplayRemoveClasses || '',
        addAttributes: matchedIsFalse
          ? element.conditionDisplayRemoveAttributes || ''
          : element.conditionDisplayAddAttributes || '',
        removeAttributes: matchedIsFalse
          ? element.conditionDisplayAddAttributes || ''
          : element.conditionDisplayRemoveAttributes || ''
      };

      switch (element.conditionDisplayAction) {
        case 'modify': {
          setConditionalDisplayResult({
            action: matchedIsFalse
              ? DISPLAY_ACTION.NO_ACTION
              : DISPLAY_ACTION.MODIFY_ATTRIBUTES_OR_CLASSES,
            ...attributesAndClasses
          });
          return;
        }
        case 'hide': {
          setConditionalDisplayResult({
            action: matchedIsFalse
              ? DISPLAY_ACTION.SHOW_ELEMENT
              : DISPLAY_ACTION.HIDE_ELEMENT,
            ...attributesAndClasses
          });
          return;
        }
        case 'show': {
          setConditionalDisplayResult({
            action: matchedIsFalse
              ? DISPLAY_ACTION.HIDE_ELEMENT
              : DISPLAY_ACTION.SHOW_ELEMENT,
            ...attributesAndClasses
          });
          return;
        }
        case 'disable': {
          setConditionalDisplayResult({
            action: matchedIsFalse
              ? DISPLAY_ACTION.ENABLE_ELEMENT
              : DISPLAY_ACTION.DISABLE_ELEMENT,
            ...attributesAndClasses
          });
          return;
        }
        case 'enable': {
          setConditionalDisplayResult({
            action: matchedIsFalse
              ? DISPLAY_ACTION.DISABLE_ELEMENT
              : DISPLAY_ACTION.ENABLE_ELEMENT,
            ...attributesAndClasses
          });
          return;
        }
        case 'text': {
          if (!matchedIsFalse) {
            setConditionalDisplayResult({
              action: DISPLAY_ACTION.SET_TEXT,
              ...attributesAndClasses
            });
          }
          return;
        }
        default: {
          setConditionalDisplayResult({ action: DISPLAY_ACTION.NO_ACTION });
        }
      }
    }
    if (!designMode) {
      evaluateConditions();
    }
  }, [selections, documents, local, element, scenario, sequencer]);

  return {
    DISPLAY_ACTION,
    conditionalDisplayResult
  };
};

export default useConditionalDisplay;
